import * as React from "react";
import cx from "classnames";
import moment from "moment";
import { useAuth } from "../../auth";
import { isLeadership } from "../../lib/access";

import {
  toolBelt,
  dailyChecklist,
  weeklyChecklist,
  playbooks,
  protocols,
  notebookTopics,
  podcast,
  IQuickLink,
  IChecklistGroup,
  IChecklistItem
} from "./content";
import {
  useCoachPerformance,
  ALLOWED_SUBDOMAINS,
  formatCents,
  formatRate,
  formatNumber,
  membersOnLongHold,
  cancellationsInMonth,
  invoicesDueSoon,
  gymDisplayName,
  monthDisplayName,
  coachAcrossMonths,
  averageField,
  getShowRate,
  getConversionRate,
  getFreeSessionsBooked,
  getFreeSessionsAttended,
  getFreeSessionsConverted,
  derivePricing,
  ILivePricingRow,
  buildComplianceFromCoachApi,
  buildAllCoachesAggregate,
  ALL_COACHES_ID
} from "./use-coach-performance";
import {
  ICoach,
  ICoachMember,
  IUnattributedMember
} from "./coach-performance.types";
import GymWideSection from "./gym-wide-section";
import { Free30Drilldown } from "./Free30Drilldown";
import plansData from "../../data/plans.json";
import {
  getDailyCompliance,
  getWeeklyCompliance
} from "./use-checklist-compliance";

import styles from "./dashboard.module.css";

const CHECKLIST_STORAGE_PREFIX = "coach-dashboard:checklist:";

function todayKey() {
  return moment().format("YYYY-MM-DD");
}

function weekKey() {
  return moment().format("GGGG-[W]WW");
}

function useChecklistState(scope: string) {
  const storageKey = `${CHECKLIST_STORAGE_PREFIX}${scope}`;

  const [checked, setChecked] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const toggle = React.useCallback(
    (id: string) => {
      setChecked(prev => {
        const next = { ...prev, [id]: !prev[id] };
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // storage is best-effort; fail quietly
        }
        return next;
      });
    },
    [storageKey]
  );

  return { checked, toggle };
}

/* ------------------------------- KPI cards ------------------------------- */

/* ----------------- Top-line headline tiles (Row 1) ---------------------- */

interface IHeadlineTile {
  label: string;
  value: string;
  hint?: string;
}

function HeadlineTiles({ tiles }: { tiles: IHeadlineTile[] }) {
  return (
    <div className={styles.headlineGrid}>
      {tiles.map(t => (
        <div key={t.label} className={styles.headlineTile}>
          <span className={styles.headlineLabel}>{t.label}</span>
          <span className={styles.headlineValue}>{t.value}</span>
          {t.hint && <span className={styles.headlineHint}>{t.hint}</span>}
        </div>
      ))}
    </div>
  );
}

/* ----------------- Period-to-date count tiles (Row 2) ------------------- */

interface ICountTile {
  label: string;
  value: string;
}

function CountTiles({ tiles }: { tiles: ICountTile[] }) {
  return (
    <div className={styles.countGrid}>
      {tiles.map(t => (
        <div key={t.label} className={styles.countTile}>
          <span className={styles.countLabel}>{t.label}</span>
          <span className={styles.countValue}>{t.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ----------------- Free 30 compliance tiles (Row 3) --------------------- */

interface IComplianceTile {
  label: string;
  rate: string;
  detail: string;
}

function ComplianceTiles({ tiles }: { tiles: IComplianceTile[] }) {
  return (
    <div className={styles.complianceGrid}>
      {tiles.map(t => (
        <div key={t.label} className={styles.complianceTile}>
          <span className={styles.complianceLabel}>{t.label}</span>
          <span className={styles.complianceRate}>{t.rate}</span>
          <span className={styles.complianceDetail}>{t.detail}</span>
        </div>
      ))}
    </div>
  );
}

/* ----------------- Action-item summary tiles (Row 4) -------------------- */

interface IActionTile {
  label: string;
  value: string;
  hint?: string;
  hot?: boolean;
}

function ActionTiles({ tiles }: { tiles: IActionTile[] }) {
  return (
    <div className={styles.actionTileGrid}>
      {tiles.map(t => (
        <div
          key={t.label}
          className={cx(styles.actionTile, {
            [styles.actionTileHot]: t.hot
          })}
        >
          <span className={styles.actionTileLabel}>{t.label}</span>
          <span className={styles.actionTileValue}>{t.value}</span>
          {t.hint && <span className={styles.actionTileHint}>{t.hint}</span>}
        </div>
      ))}
    </div>
  );
}

/* --------------------------- Link card primitives ------------------------- */

function LinkCard({ link }: { link: IQuickLink }) {
  const isExternal = link.url.startsWith("http");
  const isPlaceholder = link.url === "#";

  return (
    <a
      href={link.url}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className={cx(styles.linkCard, {
        [styles.linkCardPrimary]: link.urgency === "primary"
      })}
      onClick={isPlaceholder ? e => e.preventDefault() : undefined}
      aria-disabled={isPlaceholder}
    >
      <h3 className={styles.linkTitle}>{link.title}</h3>
      <p className={styles.linkDescription}>{link.description}</p>
      <span className={styles.linkArrow}>
        {isPlaceholder ? "Link coming soon" : "Open →"}
      </span>
    </a>
  );
}

function LinkGrid({ links }: { links: IQuickLink[] }) {
  return (
    <div className={styles.cardGrid}>
      {links.map(link => (
        <LinkCard key={link.id} link={link} />
      ))}
    </div>
  );
}

/* ------------------------------- Checklist ------------------------------- */

interface IDailyGroupProps {
  group: IChecklistGroup;
  checked: Record<string, boolean>;
  onToggle: (id: string) => void;
}

function DailyGroup({ group, checked, onToggle }: IDailyGroupProps) {
  const [open, setOpen] = React.useState(true);
  const done = group.items.filter(i => checked[i.id]).length;
  const total = group.items.length;
  const allDone = done === total;

  return (
    <div className={styles.checklistGroup}>
      <button
        type="button"
        className={styles.checklistGroupHeader}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
      >
        <span>{group.title}</span>
        <span
          className={cx(styles.checklistProgress, {
            [styles.checklistProgressDone]: allDone
          })}
        >
          {done}/{total}
          {open ? " ▾" : " ▸"}
        </span>
      </button>
      {open && (
        <div className={styles.checklistItems}>
          {group.items.map(item => (
            <label
              key={item.id}
              className={cx(styles.checklistItem, {
                [styles.checklistItemDone]: !!checked[item.id]
              })}
            >
              <input
                type="checkbox"
                checked={!!checked[item.id]}
                onChange={() => onToggle(item.id)}
              />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Action items ---------------------------- */

interface IActionCardProps {
  title: string;
  count: number;
  emptyText?: string;
  children: React.ReactNode;
}

function ActionCard({ title, count, emptyText, children }: IActionCardProps) {
  return (
    <div
      className={cx(styles.actionCard, { [styles.actionCardHot]: count > 0 })}
    >
      <div className={styles.actionCardHeader}>
        <span className={styles.actionCardTitle}>{title}</span>
        <span className={styles.actionCardCount}>{count}</span>
      </div>
      {count === 0 ? (
        <p className={styles.actionCardEmpty}>{emptyText ?? "All clear."}</p>
      ) : (
        <ul className={styles.actionCardList}>{children}</ul>
      )}
    </div>
  );
}

interface IExternalActionProps {
  title: string;
  count: number;
  url: string;
  hint: string;
}

function ExternalAction({ title, count, url, hint }: IExternalActionProps) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cx(styles.externalAction, {
        [styles.externalActionHot]: count > 0
      })}
    >
      <span className={styles.externalActionLabel}>{title}</span>
      <span className={styles.externalActionCount}>{count}</span>
      <span className={styles.externalActionHint}>{hint} →</span>
    </a>
  );
}

/* ----------------------------- Member roster --------------------------- */

function memberStatusLabel(m: ICoachMember): string {
  if (m.is_churned) return "Churned";
  // Only treat cancel_date as "Cancelling" when it's in the future. A past
  // cancel_date alongside is_current_member=true is a stale Pike13 field
  // from a prior cancellation that was reversed.
  if (m.cancel_date && m.cancel_date >= moment().format("YYYY-MM-DD")) {
    return "Cancelling";
  }
  if (m.hold_start) return "On hold";
  if (m.is_current_member) return "Active";
  return m.status || "—";
}

function memberKeyDate(
  m: ICoachMember
): { label: string; value: string } | null {
  const today = moment().format("YYYY-MM-DD");
  if (m.cancel_date && m.cancel_date >= today) {
    return { label: "Cancels", value: m.cancel_date };
  }
  if (m.hold_start) return { label: "Hold since", value: m.hold_start };
  if (m.invoice_due_date)
    return { label: "Next bill", value: m.invoice_due_date };
  return null;
}

function tierBadge(tier: ICoachMember["attribution_tier"]): string {
  if (tier === "tier_1") return "1";
  if (tier === "tier_2") return "2";
  return "—";
}

function unassignedStatusLabel(m: IUnattributedMember): string {
  if (m.is_churned) return "Churned";
  if (m.cancel_date && m.cancel_date >= moment().format("YYYY-MM-DD")) {
    return "Cancelling";
  }
  if (m.hold_start) return "On hold";
  if (m.status === "active") return "Active";
  return m.status || "—";
}

/**
 * Unassigned members array doesn't carry `is_current_member`, so the
 * equivalent "this is actually a member" rule is: has a `plan_name` and
 * is not churned. That filters out cancelled/null-status/test-stub rows
 * the API includes here.
 */
function isRealUnassignedMember(m: IUnattributedMember): boolean {
  return Boolean(m.plan_name) && !m.is_churned;
}

function UnassignedRoster({
  members: allMembers
}: {
  members: IUnattributedMember[];
}) {
  const [open, setOpen] = React.useState(false);
  const members = allMembers.filter(isRealUnassignedMember);
  const hiddenCount = allMembers.length - members.length;
  if (members.length === 0) return null;

  return (
    <div className={styles.rosterCard}>
      <button
        type="button"
        className={styles.rosterHeader}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>Unassigned members</span>
        <span className={styles.rosterCount}>
          {members.length}
          {hiddenCount > 0 && <span> · {hiddenCount} non-current hidden</span>}
          {open ? " ▾" : " ▸"}
        </span>
      </button>
      {open && (
        <div className={styles.rosterTableWrap}>
          <table className={styles.rosterTable}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Status</th>
                <th>Plan</th>
                <th>MRR</th>
                <th>Key date</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const status = unassignedStatusLabel(m);
                const today = moment().format("YYYY-MM-DD");
                let keyDate: { label: string; value: string } | null = null;
                if (m.cancel_date && m.cancel_date >= today) {
                  keyDate = { label: "Cancels", value: m.cancel_date };
                } else if (m.hold_start) {
                  keyDate = { label: "Hold since", value: m.hold_start };
                }
                return (
                  <tr key={m.person_id}>
                    <td>{m.name}</td>
                    <td>
                      <span
                        className={cx(styles.statusPill, {
                          [styles.statusActive]: status === "Active",
                          [styles.statusHold]: status === "On hold",
                          [styles.statusCancel]: status === "Cancelling",
                          [styles.statusChurned]: status === "Churned"
                        })}
                      >
                        {status}
                      </span>
                    </td>
                    <td>{m.plan_name ?? "—"}</td>
                    <td className={styles.rosterMrr}>
                      {formatCents(m.mrr_cents)}
                    </td>
                    <td>
                      {keyDate ? (
                        <span>
                          <span className={styles.rosterDateLabel}>
                            {keyDate.label}:
                          </span>{" "}
                          {keyDate.value}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Show only rows where the API says the person is a current member. This
 * catches everything we want to filter out — placeholder visit-ghosts,
 * actually-cancelled-and-gone, churned past members — in one rule.
 * A past `cancel_date` on a row with `is_current_member: true` is fine;
 * it's stale field state from a prior reversed cancellation.
 */
function isRealMember(m: ICoachMember): boolean {
  return m.is_current_member === true;
}

function MemberRoster({ coach }: { coach: ICoach | null }) {
  const [open, setOpen] = React.useState(false);
  const allMembers = coach?.members ?? [];
  const members = allMembers.filter(isRealMember);
  const hiddenCount = allMembers.length - members.length;
  const isAllCoaches = coach?.person_id === ALL_COACHES_ID;

  if (members.length === 0) return null;

  // Sort: active first, then on-hold, then cancelling, then churned. Within
  // each, alphabetical.
  const order: Record<string, number> = {
    Active: 0,
    "On hold": 1,
    Cancelling: 2,
    Churned: 3
  };
  const sorted = [...members].sort((a, b) => {
    const oa = order[memberStatusLabel(a)] ?? 99;
    const ob = order[memberStatusLabel(b)] ?? 99;
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className={styles.rosterCard}>
      <button
        type="button"
        className={styles.rosterHeader}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>{isAllCoaches ? "All members (gym-wide)" : "Your members"}</span>
        <span className={styles.rosterCount}>
          {members.length}
          {hiddenCount > 0 && <span> · {hiddenCount} non-current hidden</span>}
          {open ? " ▾" : " ▸"}
        </span>
      </button>
      {open && (
        <div className={styles.rosterTableWrap}>
          <table className={styles.rosterTable}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Plan</th>
                <th>MRR</th>
                <th>Key date</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(m => {
                const status = memberStatusLabel(m);
                const keyDate = memberKeyDate(m);
                return (
                  <tr key={m.person_id}>
                    <td>{m.name}</td>
                    <td className={styles.rosterTier}>
                      {tierBadge(m.attribution_tier)}
                    </td>
                    <td>
                      <span
                        className={cx(styles.statusPill, {
                          [styles.statusActive]: status === "Active",
                          [styles.statusHold]: status === "On hold",
                          [styles.statusCancel]: status === "Cancelling",
                          [styles.statusChurned]: status === "Churned"
                        })}
                      >
                        {status}
                      </span>
                    </td>
                    <td>{m.plan_name ?? "—"}</td>
                    <td className={styles.rosterMrr}>
                      {formatCents(m.mrr_cents)}
                    </td>
                    <td>
                      {keyDate ? (
                        <span>
                          <span className={styles.rosterDateLabel}>
                            {keyDate.label}:
                          </span>{" "}
                          {keyDate.value}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WeeklyList({
  items,
  checked,
  onToggle
}: {
  items: IChecklistItem[];
  checked: Record<string, boolean>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className={styles.weeklyCard}>
      {items.map(item => (
        <label
          key={item.id}
          className={cx(styles.checklistItem, {
            [styles.checklistItemDone]: !!checked[item.id]
          })}
        >
          <input
            type="checkbox"
            checked={!!checked[item.id]}
            onChange={() => onToggle(item.id)}
          />
          <span>{item.label}</span>
        </label>
      ))}
    </div>
  );
}

/* -------------------------------- Page ----------------------------------- */

export default function CoachDashboard() {
  const { user } = useAuth();
  // PILOT: only leadership reaches this page (DashboardGuard in routes.tsx
  // redirects everyone else). `leadership` stays as the gating predicate
  // here so admin-only UI surfaces (coach picker, gym selector, gym-wide
  // section) keep working without the Auth0 role claim plumbing.
  const leadership = isLeadership(user?.email);

  const daily = useChecklistState(todayKey());
  const weekly = useChecklistState(weekKey());

  const perf = useCoachPerformance();
  const coaches = perf.data?.coaches ?? [];

  const [overrideCoachId, setOverrideCoachId] = React.useState<number | null>(
    null
  );

  // Free 30 per-guest drill-down disclosure (collapsed by default).
  const [free30Open, setFree30Open] = React.useState(false);

  // Reset override + drill-down when underlying data changes (gym/month switch).
  React.useEffect(() => {
    setOverrideCoachId(null);
    setFree30Open(false);
  }, [perf.subdomain, perf.month]);

  // Default the selected coach to the "All coaches" aggregate so leadership
  // lands on a gym-wide view rather than an empty picker. They can drill
  // into an individual coach via the picker.
  let selectedCoach: ICoach | null = null;
  const isAllSelected =
    overrideCoachId === null || overrideCoachId === ALL_COACHES_ID;
  if (isAllSelected && perf.data) {
    selectedCoach = buildAllCoachesAggregate(perf.data);
  } else if (overrideCoachId) {
    selectedCoach = coaches.find(c => c.person_id === overrideCoachId) ?? null;
  }

  // Coach picker is leadership-only by virtue of this page being gated to
  // leadership in routes.tsx. Keep the conditional so the picker doesn't
  // render before data loads.
  const showCoachPicker = leadership && coaches.length > 0;

  // Same coach across the trailing 90-day window for rolling averages.
  // For the synthetic "All coaches" selection, build a synthetic aggregate
  // from each trailing month's totals block so churn rate (and any other
  // rolling-average) reflects gym-wide rolling values rather than failing
  // the person_id lookup.
  const trailingCoachRecords = React.useMemo(() => {
    if (!selectedCoach) return [];
    if (selectedCoach.person_id === ALL_COACHES_ID) {
      return perf.trailing.map(m => buildAllCoachesAggregate(m));
    }
    return coachAcrossMonths(selectedCoach.person_id, perf.trailing);
  }, [selectedCoach, perf.trailing]);

  // Show Rate and Conversion Rate: selected month only.
  // Churn Rate: 30-day averaged over a 90-day window.
  const showRateMonth = selectedCoach ? getShowRate(selectedCoach) : null;
  const conversionRateMonth = selectedCoach
    ? getConversionRate(selectedCoach)
    : null;
  const churnRate90 = averageField(trailingCoachRecords, "churn_rate");

  const headlineTiles: IHeadlineTile[] = [
    {
      label: "Show Rate",
      value: formatRate(showRateMonth),
      hint: `${monthDisplayName(perf.month)} · free sessions attended / booked`
    },
    {
      label: "Conversion Rate",
      value: formatRate(conversionRateMonth),
      hint: `${monthDisplayName(perf.month)} · converted / attended`
    },
    {
      label: "Churn Rate",
      value: formatRate(churnRate90),
      hint: "30-day average over a 90-day window · cancellations / members"
    }
  ];

  const countTiles: ICountTile[] = selectedCoach
    ? [
        {
          label: "Bookings",
          value: formatNumber(getFreeSessionsBooked(selectedCoach))
        },
        {
          label: "Shows",
          value: formatNumber(getFreeSessionsAttended(selectedCoach))
        },
        {
          label: "Conversions",
          value: formatNumber(getFreeSessionsConverted(selectedCoach))
        },
        {
          label: "Opportunities",
          value: formatNumber(selectedCoach.open_opportunities_total)
        },
        { label: "Holds", value: formatNumber(selectedCoach.holds) },
        {
          label: "Cancellations",
          value: formatNumber(selectedCoach.cancellations)
        }
      ]
    : [
        { label: "Bookings", value: "—" },
        { label: "Shows", value: "—" },
        { label: "Conversions", value: "—" },
        { label: "Opportunities", value: "—" },
        { label: "Holds", value: "—" },
        { label: "Cancellations", value: "—" }
      ];

  const openTasks = selectedCoach?.late_tasks_count ?? 0;
  const staleCount = selectedCoach?.members_no_contact_30d_count ?? 0;

  const actionTiles: IActionTile[] = [
    {
      label: "Open Tasks",
      value: formatNumber(openTasks),
      hint: "Overdue in Member Relationships",
      hot: openTasks > 0
    },
    {
      label: "Stale Leads",
      value: "—",
      hint: "Pre-booking; no contact in 7+ days"
    },
    {
      label: "Stale Guests",
      value: "—",
      hint: "Free 30 booked; no follow-up in 7+ days"
    },
    {
      label: "Stale Members",
      value: formatNumber(staleCount),
      hint:
        "30-day threshold · API field includes all GHL contacts, not strictly members",
      hot: staleCount > 0
    }
  ];

  const longHolds = membersOnLongHold(selectedCoach);
  const cancellations = cancellationsInMonth(selectedCoach, perf.month);
  const dueSoon = invoicesDueSoon(selectedCoach);
  const conciergeUrl = toolBelt.find(t => t.id === "concierge-hub")?.url ?? "#";

  // Pricing source priority:
  //   1. src/data/plans.json — snapshot from `npm run sync:plans`. Pulled
  //      directly from Pike13 by the build-time script. Most accurate.
  //   2. Coach Performance API member-MRR aggregation (fallback). Used
  //      when plans.json is empty / not yet synced for this gym.
  // The Intro Special is one-time and not in either source; it stays
  // hardcoded as a single row at the top.
  /* eslint-disable camelcase */
  interface IPike13PlanRow {
    name: string;
    price_cents: number;
    instance_count: number;
    at_mode_price: number;
    billing?: {
      interval_unit?: string | null;
      interval_count?: number | null;
    } | null;
  }
  const pike13Plans = (plansData.gyms as Record<
    string,
    { plans?: IPike13PlanRow[]; error?: string }
  >)[perf.subdomain]?.plans;
  /* eslint-enable camelcase */

  // Merge strategy: the Pike13 public Front API doesn't expose every plan
  // for every gym (SV and West Anderson hide their monthly memberships
  // from the public catalog), so we union with MRR-derived prices. The
  // catalog row wins for any plan exposed in both.
  const catalogRows: ILivePricingRow[] = (pike13Plans || []).map(p => ({
    planName: p.name,
    monthlyCents: p.price_cents,
    memberCount: p.instance_count,
    membersAtThisPrice: p.at_mode_price,
    cadence: p.billing?.interval_unit
      ? `every ${p.billing.interval_count ?? 1} ${p.billing.interval_unit}`
      : "—"
  }));
  const derivedRows: ILivePricingRow[] = perf.data
    ? derivePricing(perf.data.coaches)
    : [];
  const catalogNames = new Set(catalogRows.map(r => r.planName));
  const livePricing: ILivePricingRow[] = [
    ...catalogRows,
    ...derivedRows.filter(r => !catalogNames.has(r.planName))
  ].filter(r => !/test/i.test(r.planName));

  let pricingSource: "catalog" | "derived" | "mixed" = "derived";
  if (catalogRows.length > 0) {
    pricingSource = derivedRows.some(r => !catalogNames.has(r.planName))
      ? "mixed"
      : "catalog";
  }

  let pricingSourceLabel = "Live · awaiting Coach Performance API";
  if (pricingSource === "catalog" || pricingSource === "mixed") {
    const when = plansData.synced_at
      ? new Date(plansData.synced_at).toLocaleDateString()
      : "(never)";
    pricingSourceLabel =
      pricingSource === "mixed"
        ? `Pike13 catalog · synced ${when} · MRR-derived where catalog omits a plan`
        : `Pike13 catalog · synced ${when}`;
  } else if (perf.data) {
    pricingSourceLabel = `Live · derived from current paying members at ${gymDisplayName(
      perf.data.gym.subdomain,
      perf.data.gym.name
    )}`;
  }

  // Free 30 process compliance — now served by the Coach Performance API
  // via the `free30_process_metrics` block on each coach. No GHL fetch.
  const compliance = buildComplianceFromCoachApi(selectedCoach);
  const complianceTiles = compliance.tiles;
  const unattributedFree30 =
    perf.data?.unattributed_ghl?.free30_process_metrics
      ?.free30_leads_assigned ?? 0;
  const showUnattributedNote = isAllSelected && unattributedFree30 > 0;
  const complianceHintText = (() => {
    if (compliance.assigned === 0) {
      return "No Free 30 bookings assigned this month";
    }
    const n = compliance.assigned;
    const base = `Aggregated over ${n} Free 30${
      n === 1 ? "" : "s"
    } this month · Coach Performance API`;
    return showUnattributedNote
      ? `${base} · ${unattributedFree30} unattributed`
      : base;
  })();

  return (
    <div className={styles.page}>
      <header className={styles.greeting}>
        <p className={styles.greetingDate}>{moment().format("dddd, MMMM D")}</p>
      </header>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Your performance</h2>
          <p className={styles.sectionHint}>
            {perf.data
              ? `${gymDisplayName(
                  perf.data.gym.subdomain,
                  perf.data.gym.name
                )} · ${monthDisplayName(perf.data.gym.month)}`
              : "Coach performance"}
          </p>
        </div>

        {!perf.configured && (
          <p className={styles.kpiNotice}>
            Coach performance API not configured. Set
            <code> REACT_APP_COACH_PERF_API_URL </code>
            and
            <code> REACT_APP_COACH_PERF_API_KEY </code>
            in <code>.env.local</code>.
          </p>
        )}

        {perf.configured && (
          <div className={styles.perfControls}>
            {leadership && (
              <label className={styles.perfControl}>
                <span>Gym</span>
                <select
                  value={perf.subdomain}
                  onChange={e => perf.setSubdomain(e.target.value)}
                >
                  {ALLOWED_SUBDOMAINS.map(s => (
                    <option key={s} value={s}>
                      {gymDisplayName(s)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className={styles.perfControl}>
              <span>Month</span>
              <select
                value={perf.month}
                onChange={e => perf.setMonth(e.target.value)}
              >
                {Array.from({ length: 24 }, (_, i) =>
                  moment()
                    .subtract(i, "month")
                    .format("YYYY-MM")
                ).map(m => (
                  <option key={m} value={m}>
                    {monthDisplayName(m)}
                  </option>
                ))}
              </select>
            </label>
            {showCoachPicker && (
              <label className={styles.perfControl}>
                <span>Coach</span>
                <select
                  value={selectedCoach?.person_id ?? ""}
                  onChange={e =>
                    setOverrideCoachId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                >
                  {!selectedCoach && <option value="">— pick a coach —</option>}
                  <option value={ALL_COACHES_ID}>
                    All coaches (gym total)
                  </option>
                  {coaches.map(c => (
                    <option key={c.person_id} value={c.person_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        <div className={styles.kpiRowGroup}>
          <div className={styles.kpiRow}>
            <div className={styles.kpiRowHeader}>
              <span className={styles.kpiRowLabel}>Top-line rates</span>
            </div>
            <HeadlineTiles tiles={headlineTiles} />
          </div>

          <div className={styles.kpiRow}>
            <div className={styles.kpiRowHeader}>
              <span className={styles.kpiRowLabel}>
                Period to date · {monthDisplayName(perf.month)}
              </span>
            </div>
            <CountTiles tiles={countTiles} />
          </div>

          <div className={styles.kpiRow}>
            <div className={styles.kpiRowHeader}>
              <span className={styles.kpiRowLabel}>
                Free 30 process compliance
              </span>
              <span className={styles.kpiRowHint}>{complianceHintText}</span>
            </div>
            <ComplianceTiles tiles={complianceTiles} />
          </div>

          {selectedCoach && (
            <div className={styles.kpiRow}>
              <div className={styles.kpiRowHeader}>
                <button
                  type="button"
                  className={styles.kpiRowLabel}
                  onClick={() => setFree30Open(o => !o)}
                >
                  {free30Open ? "Hide" : "Show"} Free 30 guests
                </button>
              </div>
              {free30Open && (
                <Free30Drilldown
                  subdomain={perf.subdomain}
                  month={perf.month}
                  coach={
                    selectedCoach.person_id === ALL_COACHES_ID
                      ? null
                      : selectedCoach.name
                  }
                />
              )}
            </div>
          )}

          <div className={styles.kpiRow}>
            <div className={styles.kpiRowHeader}>
              <span className={styles.kpiRowLabel}>Action items</span>
            </div>
            <ActionTiles tiles={actionTiles} />
          </div>
        </div>

        {perf.loading && (
          <p className={styles.kpiNotice}>Loading performance data…</p>
        )}
        {perf.error && (
          <p className={cx(styles.kpiNotice, styles.kpiNoticeError)}>
            {perf.error.error}: {perf.error.message}
          </p>
        )}
        {perf.data && (
          <p className={styles.kpiNotice}>
            Synced{" "}
            {perf.data.synced_at
              ? moment(perf.data.synced_at).fromNow()
              : "recently"}
            {perf.data.totals.data_completeness === "partial" &&
              " · current month, partial data"}
          </p>
        )}
      </section>

      {selectedCoach && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Needs your attention</h2>
            <p className={styles.sectionHint}>
              Computed from your member roster
            </p>
          </div>

          <div className={styles.actionGrid}>
            <ActionCard
              title="Holds 60+ days"
              count={longHolds.length}
              emptyText="No long holds."
            >
              {longHolds.slice(0, 5).map(h => (
                <li key={h.name}>
                  <span className={styles.actionMember}>{h.name}</span>
                  <span className={styles.actionMeta}>
                    {h.days}d · since {h.holdStart}
                  </span>
                </li>
              ))}
              {longHolds.length > 5 && (
                <li className={styles.actionMore}>
                  +{longHolds.length - 5} more
                </li>
              )}
            </ActionCard>

            <ActionCard
              title="Cancellations this month"
              count={cancellations.length}
              emptyText="No cancellations."
            >
              {cancellations.slice(0, 5).map(c => (
                <li key={c.name}>
                  <span className={styles.actionMember}>{c.name}</span>
                  <span className={styles.actionMeta}>
                    {c.cancelDate}
                    {c.plan ? ` · ${c.plan}` : ""}
                  </span>
                </li>
              ))}
              {cancellations.length > 5 && (
                <li className={styles.actionMore}>
                  +{cancellations.length - 5} more
                </li>
              )}
            </ActionCard>

            <ActionCard
              title="Invoices due in 7 days"
              count={dueSoon.length}
              emptyText="Nothing due this week."
            >
              {dueSoon.slice(0, 5).map(d => (
                <li key={d.name}>
                  <span className={styles.actionMember}>{d.name}</span>
                  <span className={styles.actionMeta}>
                    {d.due} · {formatCents(d.mrrCents)}
                  </span>
                </li>
              ))}
              {dueSoon.length > 5 && (
                <li className={styles.actionMore}>
                  +{dueSoon.length - 5} more
                </li>
              )}
            </ActionCard>
          </div>

          <div className={styles.externalActionRow}>
            <ExternalAction
              title="Late tasks"
              count={selectedCoach.late_tasks_count}
              url={conciergeUrl}
              hint="Open Member Relationships"
            />
            <ExternalAction
              title="Contacts not contacted 30d"
              count={selectedCoach.members_no_contact_30d_count}
              url={conciergeUrl}
              hint="GHL contacts (leads + guests + members); population includes all pipelines"
            />
          </div>

          <UnassignedRoster
            members={perf.data?.unattributed_pike13?.members ?? []}
          />
        </section>
      )}

      {/* Checklist compliance — derived from the local-storage history
          written when the coach toggles items below. Toggling updates the
          `daily.checked` / `weekly.checked` state above, which re-renders
          this component and re-reads localStorage. */}
      {(() => {
        const dailyCompliance = getDailyCompliance(30);
        const weeklyCompliance = getWeeklyCompliance(4);
        return (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Checklist compliance</h2>
              <p className={styles.sectionHint}>
                Last 30 days · <strong>this browser only</strong> — not yet
                visible to admins. Backend persistence is on the roadmap.
              </p>
            </div>
            <div className={styles.actionTileGrid}>
              {dailyCompliance.map(c => (
                <div key={c.groupId} className={styles.actionTile}>
                  <span className={styles.actionTileLabel}>{c.groupTitle}</span>
                  <span className={styles.actionTileValue}>
                    {c.rate === null ? "—" : `${Math.round(c.rate * 100)}%`}
                  </span>
                  <span className={styles.actionTileHint}>
                    {c.itemsChecked} of {c.itemsPossible} items ·{" "}
                    {c.daysWithData} of {c.windowDays} days with data
                  </span>
                </div>
              ))}
              <div className={styles.actionTile}>
                <span className={styles.actionTileLabel}>
                  {weeklyCompliance.groupTitle}
                </span>
                <span className={styles.actionTileValue}>
                  {weeklyCompliance.rate === null
                    ? "—"
                    : `${Math.round(weeklyCompliance.rate * 100)}%`}
                </span>
                <span className={styles.actionTileHint}>
                  {weeklyCompliance.itemsChecked} of{" "}
                  {weeklyCompliance.itemsPossible} items ·{" "}
                  {weeklyCompliance.daysWithData} of{" "}
                  {weeklyCompliance.windowDays} weeks with data
                </span>
              </div>
            </div>
          </section>
        );
      })()}

      {selectedCoach && (
        <section className={styles.section}>
          <MemberRoster coach={selectedCoach} />
        </section>
      )}

      {leadership && (
        <GymWideSection
          month={perf.month}
          highlightCoachId={selectedCoach?.person_id ?? null}
        />
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Today&apos;s focus</h2>
          <p className={styles.sectionHint}>Resets each morning</p>
        </div>

        <div className={styles.headspaceCallout}>
          We are here to help people improve the quality of their existence —
          everything we do is in service of that.
        </div>

        <div className={styles.topPriority}>
          <span className={styles.topPriorityLabel}>Most important today</span>
          Reply to guests and members within 24 hours.
        </div>

        {dailyChecklist.map(group => (
          <DailyGroup
            key={group.id}
            group={group}
            checked={daily.checked}
            onToggle={daily.toggle}
          />
        ))}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Systems</h2>
        </div>
        <LinkGrid links={toolBelt} />
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Helpful Guides</h2>
          <p className={styles.sectionHint}>Step-by-step coaching guides</p>
        </div>
        <LinkGrid links={playbooks} />
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Protocols</h2>
        </div>
        <LinkGrid links={protocols} />
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>NotebookLM topics</h2>
          <p className={styles.sectionHint}>
            Ask as if asking an experienced coach
          </p>
        </div>
        <ul className={styles.topicsList}>
          {notebookTopics.map(topic => (
            <li key={topic}>{topic}</li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Podcast</h2>
        </div>
        <LinkGrid links={[podcast]} />
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Pricing reference</h2>
          <p className={styles.sectionHint}>{pricingSourceLabel}</p>
        </div>
        <table className={styles.pricingTable}>
          <thead>
            <tr>
              <th>Membership</th>
              <th>Price</th>
              <th>Source / detail</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={styles.priceProduct}>
                Intro Special{" "}
                <span className={styles.kpiSubtitle}>(one-time)</span>
              </td>
              <td className={styles.pricePrice}>$185</td>
              <td>hardcoded</td>
            </tr>
            {livePricing.map(row => {
              const isCatalog = row.cadence !== undefined;
              return (
                <tr key={row.planName}>
                  <td className={styles.priceProduct}>{row.planName}</td>
                  <td className={styles.pricePrice}>
                    {formatCents(row.monthlyCents)}/mo
                  </td>
                  <td>
                    {isCatalog ? (
                      <span>
                        Pike13 catalog
                        {row.cadence && row.cadence !== "—" && (
                          <span className={styles.kpiSubtitle}>
                            {" · "}
                            {row.cadence}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span>
                        MRR-derived · {row.memberCount} member
                        {row.memberCount === 1 ? "" : "s"}
                        {row.membersAtThisPrice < row.memberCount && (
                          <span className={styles.kpiSubtitle}>
                            {" "}
                            ({row.membersAtThisPrice} at this price)
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {livePricing.length === 0 && perf.data && (
              <tr>
                <td colSpan={3} className={styles.placeholderCell}>
                  No paying members in this gym this month.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>This week</h2>
          <p className={styles.sectionHint}>Resets every Monday</p>
        </div>
        <WeeklyList
          items={weeklyChecklist}
          checked={weekly.checked}
          onToggle={weekly.toggle}
        />
      </section>
    </div>
  );
}
